"use client";

import { useEffect } from "react";
import { AlertTriangle } from "lucide-react";
import { capturePostHogException } from "@/lib/posthog-capture";
import { recoverFromChunkLoadError } from "@/lib/chunk-load-recovery";
import {
  recoverFromClerkFrontendLoadError,
  retryAfterClerkOrChunkError,
} from "@/lib/clerk-load-recovery";

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    capturePostHogException(error, {
      source: "next_global_error_boundary",
      error_digest: error.digest,
    });
    recoverFromChunkLoadError(error);
    recoverFromClerkFrontendLoadError(error);
  }, [error]);

  return (
    <html lang="en">
      <body
        style={{
          background: "#0e0c0a",
          minHeight: "95vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <main
          style={{
            maxWidth: "32rem",
            width: "100%",
            padding: "0 1.5rem",
            textAlign: "center",
          }}
        >
          {/* Icon */}
          <div
            style={{
              background: "rgba(201,168,76,0.1)",
              border: "1px solid rgba(201,168,76,0.2)",
              width: "4rem",
              height: "4rem",
              borderRadius: "1rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 1.5rem",
            }}
          >
            <AlertTriangle
              style={{ width: "2rem", height: "2rem", color: "#c9a84c" }}
            />
          </div>

          {/* Label */}
          <p
            style={{
              color: "#c9a84c",
              fontSize: "11px",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.13em",
              marginBottom: "0.75rem",
            }}
          >
            Something went wrong
          </p>

          {/* Heading */}
          <h1
            style={{
              color: "#faf6ee",
              fontSize: "clamp(28px, 5vw, 36px)",
              fontWeight: 900,
              lineHeight: 1.1,
              letterSpacing: "-0.025em",
              marginBottom: "0.75rem",
              fontFamily: "Georgia, serif",
            }}
          >
            We hit an{" "}
            <em style={{ fontStyle: "italic", color: "#c9a84c" }}>
              unexpected
            </em>{" "}
            error.
          </h1>

          {/* Body */}
          <p
            style={{
              color: "rgba(250,246,238,0.55)",
              fontSize: "15px",
              lineHeight: 1.6,
              marginBottom: "2rem",
            }}
          >
            The issue has been captured so we can investigate it. Please try
            again.
          </p>

          {/* CTAs */}
          <div
            style={{
              display: "flex",
              gap: "0.75rem",
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <button
              type="button"
              onClick={() => {
                retryAfterClerkOrChunkError(error, reset);
              }}
              style={{
                background: "linear-gradient(135deg, #c9a84c, #e8c96a)",
                color: "#0e0c0a",
                borderRadius: "9999px",
                padding: "0.75rem 1.5rem",
                fontSize: "14px",
                fontWeight: 600,
                border: "none",
                cursor: "pointer",
              }}
            >
              Try again
            </button>
            <button
              type="button"
              onClick={() => {
                window.location.assign("/parties");
              }}
              style={{
                border: "1px solid rgba(250,246,238,0.18)",
                color: "rgba(250,246,238,0.75)",
                borderRadius: "9999px",
                padding: "0.75rem 1.5rem",
                fontSize: "14px",
                fontWeight: 600,
                background: "transparent",
                textDecoration: "none",
                display: "inline-flex",
                alignItems: "center",
                cursor: "pointer",
              }}
            >
              Go to your parties
            </button>
          </div>
        </main>
      </body>
    </html>
  );
}
